import { useEffect, useMemo, useRef, useState } from 'react';
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from './ui/drawer';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Star, ThumbsUp, ThumbsDown, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Taste-personalization questions (Business Rule #37). Friendly, easy-to-answer
// phrasing. Each option's value is the stored `coffee_preferences` value — the
// "corrective" options drive a per-product hidden barista note; the neutral
// option ("normal" / "moderate" / "0") marks the dimension answered with no note.
// Sugar sachet is a COUNT (None / 1 / 2) carried verbatim into the note.
const PERSO_QUESTIONS = {
  sweetness: {
    title: 'How do you like the sweetness in your drink?',
    options: [['less', 'Less sweet'], ['normal', 'Just right'], ['more', 'A bit sweeter']],
  },
  strength: {
    title: 'How strong do you like your coffee?',
    options: [['light', 'Milder'], ['moderate', 'Just right'], ['strong', 'Stronger']],
  },
  // The sachet question is intentionally cappuccino-specific (it's the only
  // sachet_eligible product) — admin controls where it appears via the
  // per-product sachet flag; this copy assumes that scoping.
  sugar_sachet: {
    title: 'Would you like a sugar sachet mixed in your cappuccino going forward?',
    options: [['0', 'No, without sugar is ok.'], ['1', '1 sachet'], ['2', '2 sachet']],
  },
};

/**
 * Stage 2 of the Swiggy-style home-screen review (Business Rule #19).
 *
 * Opened from `ReviewPrompt` after the customer taps `Add detailed feedback`.
 * Posts to the same `POST /api/orders/:id/rate` endpoint — the backend treats
 * the second submission as an append (preserves the original star count, adds
 * `item_ratings` + `feedback`).
 *
 * @param {object} order — the order being rated; expects enriched `items[].name`
 * @param {string} token — auth bearer
 * @param {number} initialRating — stars set by the prompt; user can still tweak
 * @param {boolean} isOpen — controlled open state
 * @param {(submitted: boolean) => void} onClose — called when sheet closes
 */
export default function ReviewDetailSheet({
  order, token, initialRating, isOpen, onClose,
  askDimensions = [], consentRequired = false, consentVersion,
}) {
  const [rating, setRating] = useState(initialRating || 0);
  const [feedback, setFeedback] = useState('');
  const [itemSentiment, setItemSentiment] = useState({}); // { menu_item_id: 'up' | 'down' }
  const [persoAnswers, setPersoAnswers] = useState({}); // { dimension: value } (Business Rule #37)
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  // Guards the vaul double-fire: handleSubmit calls onClose(true) which flips
  // isOpen → the Drawer's onOpenChange(false) fires too. Without this, a
  // successful submit would also dismiss the just-answered dimensions.
  const submittedRef = useRef(false);

  const distinctItems = useMemo(() => buildDistinctItems(order), [order]);
  const askList = useMemo(
    () => (askDimensions || []).filter((d) => PERSO_QUESTIONS[d]),
    [askDimensions],
  );

  useEffect(() => {
    if (isOpen) {
      setRating(initialRating || 0);
      setFeedback('');
      setItemSentiment({});
      setPersoAnswers({});
      setSubmitting(false);
      setErrors({});
      submittedRef.current = false;
    }
  }, [isOpen, order?.id, initialRating]);

  // Fire-and-forget dismiss for asked dimensions the customer didn't answer, so
  // the cadence advances (re-ask in N qualifying visits) — Business Rule #37.
  const dismissUnanswered = (answered) => {
    for (const dim of askList) {
      if (answered[dim]) continue;
      axios.post(
        `${API}/personalization/dismiss`,
        { dimension: dim },
        { headers: { Authorization: `Bearer ${token}` } },
      ).catch(() => {});
    }
  };

  const handleDrawerClose = () => {
    // Only dismiss when the sheet was closed WITHOUT submitting (X / backdrop).
    if (!submittedRef.current) dismissUnanswered({});
    onClose(false);
  };

  const setPerso = (dim, value) => {
    setPersoAnswers((prev) => ({ ...prev, [dim]: value }));
    if (errors.content) setErrors((p) => ({ ...p, content: undefined }));
  };

  const toggleSentiment = (itemId, sentiment) => {
    if (errors.content) setErrors((p) => ({ ...p, content: undefined }));
    setItemSentiment((prev) => {
      const next = { ...prev };
      if (next[itemId] === sentiment) {
        delete next[itemId];
      } else {
        next[itemId] = sentiment;
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (submitting || !order?.id) return;
    const itemRatings = distinctItems
      .filter((row) => itemSentiment[row.menu_item_id])
      .map((row) => ({
        item_id: row.menu_item_id,
        item_name: row.name,
        sentiment: itemSentiment[row.menu_item_id],
      }));
    const hasDetail = itemRatings.length > 0 || !!feedback.trim();
    const persoEntries = Object.entries(persoAnswers);
    const hasPerso = persoEntries.length > 0;
    if (!hasDetail && !hasPerso) {
      setErrors({ content: 'Add an item rating, a note, or a taste preference' });
      return;
    }
    // A star is only needed when appending rating detail; a personalization-only
    // submit doesn't need one (the star was already set in stage 1, immutable).
    if (hasDetail && rating < 1) {
      setErrors({ rating: 'Please select a star rating' });
      return;
    }
    setSubmitting(true);
    try {
      if (hasDetail) {
        await axios.post(
          `${API}/orders/${order.id}/rate`,
          {
            rating,
            feedback: feedback.trim() || null,
            item_ratings: itemRatings.length > 0 ? itemRatings : null,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }
      // Record taste-personalization answers (Business Rule #37). Consent is
      // bundled into the first answer server-side.
      for (const [dim, value] of persoEntries) {
        await axios.post(
          `${API}/personalization/answer`,
          { dimension: dim, value, consent_version: consentVersion },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }
      submittedRef.current = true;
      // Asked-but-unanswered dimensions still advance the cadence.
      dismissUnanswered(persoAnswers);
      // Success confirmation is the sheet closing — no toast.
      onClose(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrors({ content: typeof detail === 'string' ? detail : 'Could not save feedback' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && handleDrawerClose()} shouldScaleBackground={false}>
      <DrawerContent
        className="h-[88vh] [height:88dvh] flex flex-col"
        data-testid="review-detail-sheet"
      >
        <button
          type="button"
          onClick={handleDrawerClose}
          className="absolute top-3 right-3 p-2 rounded-full text-muted-foreground hover:bg-muted transition z-10"
          aria-label="Close detailed feedback"
          data-testid="review-detail-close"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>

        <div className="flex-1 overflow-y-auto px-6 pt-2 pb-4">
          <div className="max-w-md mx-auto">
            <DrawerTitle className="font-serif text-2xl text-center">
              Tell us more
            </DrawerTitle>
            <DrawerDescription className="text-center mt-1">
              {order?.branch_name ? `${order.branch_name} · ` : ''}
              Your feedback shapes the next cup.
            </DrawerDescription>

            <div className="flex justify-center gap-1 mt-5">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => { setRating(star); if (errors.rating) setErrors((p) => ({ ...p, rating: undefined })); }}
                  className="p-1 transition-transform active:scale-95"
                  aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                  data-testid={`review-detail-star-${star}`}
                >
                  <Star
                    className={`w-9 h-9 transition-colors ${
                      star <= rating
                        ? 'fill-accent text-accent'
                        : 'text-muted-foreground/40'
                    }`}
                    strokeWidth={1.5}
                  />
                </button>
              ))}
            </div>
            {errors.rating && (
              <p className="text-xs text-destructive animate-shake text-center mt-2" role="alert" data-testid="review-detail-rating-error">{errors.rating}</p>
            )}

            {distinctItems.length > 0 && (
              <div className="mt-7">
                <h3 className="font-serif text-base text-foreground">
                  How was each item?
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Tap a thumb to flag what worked and what didn't.
                </p>
                <ul className="mt-3 space-y-2">
                  {distinctItems.map((row) => {
                    const sentiment = itemSentiment[row.menu_item_id];
                    return (
                      <li
                        key={row.menu_item_id}
                        className="flex items-center gap-3 rounded-2xl border border-border bg-card px-3 py-2.5"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {row.name}
                          </p>
                          {row.quantity > 1 && (
                            <p className="text-xs text-muted-foreground">
                              x{row.quantity}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <SentimentButton
                            active={sentiment === 'up'}
                            tone="up"
                            onClick={() => toggleSentiment(row.menu_item_id, 'up')}
                            testId={`review-detail-thumbsup-${row.menu_item_id}`}
                          />
                          <SentimentButton
                            active={sentiment === 'down'}
                            tone="down"
                            onClick={() => toggleSentiment(row.menu_item_id, 'down')}
                            testId={`review-detail-thumbsdown-${row.menu_item_id}`}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="mt-7">
              <label
                htmlFor="review-detail-feedback"
                className="font-serif text-base text-foreground block"
              >
                Tell us more
              </label>
              <Textarea
                id="review-detail-feedback"
                value={feedback}
                onChange={(e) => { setFeedback(e.target.value); if (errors.content) setErrors((p) => ({ ...p, content: undefined })); }}
                placeholder="What stood out — good or bad?"
                rows={4}
                className="mt-2 resize-none"
                data-testid="review-detail-feedback"
              />
            </div>

            {/* Taste personalization (Business Rule #37) — quietly tailors future cups. */}
            {askList.length > 0 && (
              <div className="mt-7 rounded-2xl border border-accent/30 bg-accent/5 p-4" data-testid="personalization-section">
                <h3 className="font-serif text-base text-foreground">Make it yours</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  We'll tailor how your drinks are made next time.
                </p>
                <div className="mt-3 space-y-4">
                  {askList.map((dim) => {
                    const q = PERSO_QUESTIONS[dim];
                    return (
                      <div key={dim} data-testid={`perso-q-${dim}`}>
                        <p className="text-sm font-medium text-foreground">{q.title}</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {q.options.map(([value, label]) => {
                            const on = persoAnswers[dim] === value;
                            return (
                              <button
                                key={value}
                                type="button"
                                onClick={() => setPerso(dim, value)}
                                className={`px-3 py-1.5 rounded-full text-sm border transition active:scale-95 ${
                                  on
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : 'bg-background text-muted-foreground border-input hover:border-primary/50'
                                }`}
                                data-testid={`perso-opt-${dim}-${value}`}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {consentRequired && (
                  <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                    By answering, you let us tailor future orders to your taste. You can
                    change this anytime in Profile → Preferences.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div
          className="border-t border-border bg-background px-6 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]"
        >
          <div className="max-w-md mx-auto">
            {errors.content && (
              <p className="text-xs text-destructive animate-shake text-center mb-2" role="alert" data-testid="review-detail-content-error">{errors.content}</p>
            )}
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full rounded-full"
              data-testid="review-detail-submit"
            >
              {submitting ? 'Saving…' : 'Submit feedback'}
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function SentimentButton({ active, tone, onClick, testId }) {
  const Icon = tone === 'up' ? ThumbsUp : ThumbsDown;
  const activeClasses =
    tone === 'up'
      ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
      : 'bg-rose-100 text-rose-700 border-rose-200';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-10 h-10 rounded-full border flex items-center justify-center transition active:scale-95 ${
        active ? activeClasses : 'bg-muted/40 text-muted-foreground border-transparent'
      }`}
      aria-pressed={active}
      aria-label={tone === 'up' ? 'Loved it' : 'Not for me'}
      data-testid={testId}
    >
      <Icon className="w-5 h-5" strokeWidth={1.75} />
    </button>
  );
}

/**
 * Collapse `order.items` into one row per distinct `menu_item_id`. Quantities
 * are summed across cart-line dupes so a customer who ordered 2× cappuccino +
 * 1× croissant sees two rows (`Cappuccino x2` + `Croissant`).
 */
function buildDistinctItems(order) {
  if (!order?.items) return [];
  const map = new Map();
  for (const item of order.items) {
    if (!item?.menu_item_id) continue;
    const existing = map.get(item.menu_item_id);
    if (existing) {
      existing.quantity += Number(item.quantity) || 1;
    } else {
      map.set(item.menu_item_id, {
        menu_item_id: item.menu_item_id,
        name: item.name || item.menu_item_name || 'Item',
        quantity: Number(item.quantity) || 1,
      });
    }
  }
  return Array.from(map.values());
}
